/* eslint-disable max-len */
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useProfily from 'hooks/useProfily';

import { dokJinaOsobyGet, dokumentJinaPrehled } from 'services/dokumenty/prikazy';
import {
    IDokument,
    IDokumentyPrehled,
    dokumentDownload,
    dokumentOsobyPDFdownload,
    dokumentPrehledApi,
    dokumentZmenaStavuApi
} from 'services/dokumenty/smlouva';

import { EStavZadostiDok, ETypDokumentu } from 'enums/dokumentyEnums';
import { ENotificationType } from 'enums/notificationType';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

interface IHistorieDokumenty {}

const HistorieDokumenty: React.FC<IHistorieDokumenty> = () => {
    const { language, aktivniFirma, NotificationComponent, dokumenty, profily } = useContext(Context);
    const { setLevel } = useProfily();
    const [zadosti, setZadosti] = useState<IDokumentyPrehled>();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [offset, setOffset] = useState<number>(0);
    const [refetch, setRefetch] = useState<boolean>(false);

    const columns = [
        {
            title: <TranslateText>EMP_PODANE_ZADOSTI_DATUM_PODANI</TranslateText>,
            dataIndex: 'zmenadatum',
            key: 'zmenadatum',
            render: (text: string) => dayjs(text).format('DD.MM.YYYY')
        },
        {
            title: <TranslateText>TYP_DOKUMENTU</TranslateText>,
            dataIndex: 'podtyp',
            key: 'podtyp',
            render: (text: string, row: IDokument) =>
                `${getLanguageObject(language)[text ? (row.typ === ETypDokumentu.PRIKAZY ? 'DOKUMENTY_PRIKAZ_' + text : 'TYP_SMLOUVY_' + text) : 'NOVY_DOKUMNET']}${
                    row.druhcp ? ' (' + getLanguageObject(language)['ISIN_EMISE_DRUH_' + row.druhcp] + ')' : ''
                }`,
            width: 360
        },
        {
            title: <TranslateText>EMP_PODANE_ZADOSTI_STAV</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',

            render: (text: string) => <TranslateText>{`DOKUMENTY_STAV_${text}`}</TranslateText>
        },
        {
            dataIndex: 'podtyp',
            key: 'podtyp',
            render: (text: string, row: IDokument, index: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        flexGrow: 1
                    }}
                >
                    {row.stav && !['ROZP', 'KDOPLNENI', 'DOPL', 'KDOPLNENI', 'ODESLANO'].includes(row.stav) && (
                        <AnchorComponent
                            id={`historickeZadostiZobrazitBtn${index}`}
                            onClick={() => {
                                const downloadFileName = `${aktivniFirma?.ic} - ${row?.isin ? row?.isin + ' - ' : ''}${getLanguageObject(language)[(row.typ === ETypDokumentu.PRIKAZY ? 'DOKUMENTY_PRIKAZ_' : 'TYP_SMLOUVY_') + text]}.pdf`;
                                setLoading(true);

                                if (aktivniFirma?.typ === EProfilyTyp.JINAOSOBA) {
                                    dokJinaOsobyGet(row.iddok)
                                        .then(response => {
                                            const osobaId = response.osoby.filter(
                                                e => String(e.uzivateloid) === String(profily?.id)
                                            )[0].id;
                                            dokumentOsobyPDFdownload(
                                                row.iddok,
                                                osobaId,
                                                downloadFileName,
                                                true
                                            ).finally(() => setLoading(false));
                                        })
                                        .catch(() => setLoading(false));
                                } else {
                                    dokumentDownload(row.iddok, downloadFileName).finally(() => setLoading(false));
                                }
                            }}
                        >
                            <TranslateText>STAHNOUT</TranslateText>
                        </AnchorComponent>
                    )}
                    {row.stav && ['AKCEPTOVANO', 'ZPRACOVANO', 'HOTOVOIIS'].includes(row.stav) && (
                        <>
                            {row.typ !== ETypDokumentu.PRIKAZY && (
                                <AnchorComponent
                                    id={`historickeZadostiStahnoutBtn${index}`}
                                    href={
                                        window.location.href.includes('test')
                                            ? 'https://test.cdcp.cz/wp-content/uploads/md/Plna_%20moc_k_%20zastupovani_CDCP.pdf'
                                            : 'https://www.cdcp.cz/wp-content/uploads/md/Plna_%20moc_k_%20zastupovani_CDCP.pdf'
                                    }
                                    download="Plna_moc_k_zastupovani_CDCP.pdf"
                                    target="_blank"
                                >
                                    <TranslateText>FORMULARE_PLNAMOC</TranslateText>
                                </AnchorComponent>
                            )}
                        </>
                    )}
                    {(row.stav === EStavZadostiDok.ROZP ||
                        row.stav === EStavZadostiDok.DOPL ||
                        row.stav === EStavZadostiDok.ZKONTROLOVANO ||
                        row.stav === EStavZadostiDok.KDOPLNENI) && (
                        <>
                            <AnchorComponent
                                link={true}
                                to={aktivniFirma ? `/firma/${aktivniFirma?.iducet}${routePathEnum.DOKUMENTY_NOVY}` : ''}
                                onClick={() =>
                                    setLevel('dokumenty', {
                                        ...dokumenty,
                                        selectedZadost: row,
                                        prehledData: zadosti
                                    })
                                }
                            >
                                <TranslateText>POKRACOVAT</TranslateText>
                            </AnchorComponent>
                            <AnchorComponent
                                link={true}
                                onClick={() => {
                                    setLoading(true);
                                    dokumentZmenaStavuApi(
                                        row.iddok,
                                        EStavZadostiDok.ZRUSENO,
                                        aktivniFirma?.typ === EProfilyTyp.JINAOSOBA
                                    )
                                        .then(() => {
                                            NotificationComponent({
                                                title: 'ZRUSIT_ZADOST',
                                                description: 'ZRUSIT_ZADOST_SUCCESS',
                                                type: ENotificationType.SUCCESS
                                            });
                                        })
                                        .finally(() => {
                                            setLoading(false);
                                            setRefetch(pv => !pv);
                                        });
                                }}
                            >
                                <TranslateText>ZRUSIT_ZADOST</TranslateText>
                            </AnchorComponent>
                        </>
                    )}
                </div>
            )
        }
    ];

    useEffect(() => {
        // nacitanie dat pri zmene strany v table
        setLoading(true);
        setErrStatus(0);
        (aktivniFirma?.typ === EProfilyTyp.JINAOSOBA
            ? dokumentJinaPrehled({
                  order: 'zmenadatum:desc',
                  limit: 10,
                  offset: offset
              })
            : dokumentPrehledApi({
                  order: 'zmenadatum:desc',
                  limit: 10,
                  offset: offset
              })
        )
            .then(resp => {
                setZadosti(resp);
            })
            .catch((err: any) => {
                setErrStatus(err.response?.status);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, refetch]);

    return (
        <>
            {errStatus && errStatus !== 404 ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        <LoadingWrapper loading={loading}>
                            <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                        </LoadingWrapper>
                    </Col>
                </Row>
            ) : (
                <LoadingWrapper loading={loading}>
                    <Title level={3} text="PODANE_DOKUMENTY" />
                    <Row gutter={8}>
                        <Col span={24}>
                            <TableComponent
                                columns={columns}
                                rowKey={record => record.iddok}
                                dataSource={zadosti?.dokumenty}
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                                pagination={{
                                    total: zadosti?.count,
                                    onChange: page => setOffset((page - 1) * 10),
                                    current: offset / 10 + 1,
                                    showSizeChanger: false
                                }}
                                rowClassName={(_a, i) => {
                                    const classes = [];
                                    if (i % 2) classes.push('tableRowGray');
                                    return classes.join(' ');
                                }}
                            />
                        </Col>
                    </Row>
                </LoadingWrapper>
            )}
        </>
    );
};

export default HistorieDokumenty;
